import React, { useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useLocation, useNavigate } from 'react-router-dom';

import { Alert, Button, LinearProgress, Link, Stack, Typography } from '@mui/material';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid';

import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM } from '~/consts/roles';

import {
  CHAT_REVIEW_BATCH_SIZE,
  ChatAccountFilter,
  ChatReviewRow,
  useChatReviewList,
} from '~/hooks/apis/trip-planner/useChatReviewList';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import { formatDateSlashesWithClockInSydney } from '~/services/TimeService';
import UsersService from '~/services/UsersService';

import TripPlannerChatSearchForm from './components/TripPlannerChatSearchForm';
import { TripPlannerDataGrid } from './components/TripPlannerDataGrid';
import TripSpoofDialog from './components/TripSpoofDialog';
import { TripPlannerChatSearchParams, useTripPlannerChatSearchParams } from './useTripPlannerChatSearchParams';

export default function TripPlannerChatConversationsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const gridApiRef = useGridApiRef();
  const { enqueueSnackbar } = useSnackbar();
  const { tenant } = useCurrentTenant();
  const { searchParams } = useTripPlannerChatSearchParams();
  const params = new URLSearchParams(location.search);
  const account: ChatAccountFilter =
    params.get('account') === 'registered' ? 'registered' : params.get('account') === 'guest' ? 'guest' : 'all';
  const minMessages = Math.max(0, Math.floor(Number(params.get('minMessages')) || 0));
  const targetCount = Math.max(CHAT_REVIEW_BATCH_SIZE, Number(params.get('reviewLimit')) || CHAT_REVIEW_BATCH_SIZE);
  const query = useChatReviewList({ ...searchParams, account, minMessages, brand: tenant.brand });
  const [paused, setPaused] = useState(false);
  const [spoof, setSpoof] = useState({ url: '', name: '', copied: false });
  const rows = useMemo(
    () =>
      Array.from(new Map((query.data?.pages.flatMap((page) => page.rows) ?? []).map((row) => [row.id, row])).values()),
    [query.data],
  );
  const scanned = query.data?.pages.reduce((count, page) => count + page.scanned, 0) ?? 0;
  const searching = !paused && !query.isError && (query.isFetching || (rows.length < targetCount && query.hasNextPage));
  const { fetchNextPage, hasNextPage, isFetching, isError } = query;

  useEffect(() => {
    if (!paused && !isFetching && !isError && hasNextPage && rows.length < targetCount) void fetchNextPage();
  }, [paused, isFetching, isError, hasNextPage, rows.length, targetCount, fetchNextPage]);

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(location.search);
    next.set(key, value);
    next.delete('page');
    next.delete('reviewLimit');
    setPaused(false);
    navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
  };
  const handleSearch = (values: TripPlannerChatSearchParams) => {
    const next = new URLSearchParams(location.search);
    for (const key of ['chatId', 'userId'] as const) {
      if (values[key]) next.set(key, values[key]);
      else next.delete(key);
    }
    next.delete('reviewLimit');
    next.delete('page');
    setPaused(false);
    navigate({ pathname: location.pathname, search: next.toString() });
  };
  const loadMore = () => {
    const next = new URLSearchParams(location.search);
    next.set('reviewLimit', String(rows.length + CHAT_REVIEW_BATCH_SIZE));
    setPaused(false);
    navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
  };
  const spoofChat = async (chat: ChatReviewRow) => {
    try {
      const url = await UsersService.spoofUserTrip(
        chat.createdBy,
        tenant,
        '/trip-planner/chat-trip-planner/' + chat.id,
      );
      let copied = false;
      try {
        await navigator.clipboard.writeText(url);
        copied = true;
      } catch {
        enqueueSnackbar('Copy the spoof link from the dialog.', { variant: 'info' });
      }
      setSpoof({ url, name: chat.name, copied });
    } catch {
      enqueueSnackbar('Could not generate a spoof link. Please try again.', { variant: 'error' });
    }
  };
  const columns: Array<GridColDef<ChatReviewRow>> = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
    ...(['createdAt', 'updatedAt'] as const).map<GridColDef<ChatReviewRow>>((field) => ({
      field,
      headerName: field === 'createdAt' ? 'Created (Sydney)' : 'Updated (Sydney)',
      width: 190,
      display: 'flex',
      valueFormatter: (value: string) => (value ? formatDateSlashesWithClockInSydney(value) : ''),
    })),
    { field: 'id', headerName: 'Chat ID', width: 100 },
    { field: 'userMessages', headerName: 'User messages', type: 'number', width: 140 },
    { field: 'totalMessages', headerName: 'Total messages', type: 'number', width: 140 },
    {
      field: 'createdBy',
      headerName: 'User',
      width: 120,
      sortable: false,
      renderCell: ({ row }) =>
        row.registeredUser ? (
          <Link href={'/users/' + row.createdBy} onClick={(event) => event.stopPropagation()}>
            View user
          </Link>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Guest
          </Typography>
        ),
      display: 'flex',
    },
    {
      field: 'spoofTrip',
      headerName: '',
      width: 130,
      sortable: false,
      filterable: false,
      display: 'flex',
      renderCell: ({ row }) =>
        row.registeredUser && (
          <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]}>
            <Button
              sx={{ textTransform: 'none' }}
              onClick={(event) => {
                event.stopPropagation();
                void spoofChat(row);
              }}
            >
              Spoof chat
            </Button>
          </PermissionedComponent>
        ),
    },
  ];
  return (
    <>
      <TripPlannerChatSearchForm
        initialValues={searchParams}
        onSearch={handleSearch}
        account={account}
        minMessages={minMessages}
        onAccountChange={(value) => updateFilter('account', value)}
        onMinMessagesChange={(value) => updateFilter('minMessages', value)}
      />
      <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {rows.length} matches · {scanned.toLocaleString()} chats checked
          {query.data?.pages[0] ? ' of ' + query.data.pages[0].total.toLocaleString() : ''}
        </Typography>
        {searching && (
          <Button size="small" sx={{ textTransform: 'none' }} onClick={() => setPaused(true)}>
            Pause search
          </Button>
        )}
      </Stack>
      {searching && <LinearProgress aria-label="Searching chats across pages" sx={{ mb: 1 }} />}
      {query.isError && (
        <Alert
          severity="error"
          action={
            <Button onClick={() => void (query.isFetchNextPageError ? query.fetchNextPage() : query.refetch())}>
              Retry
            </Button>
          }
        >
          Could not finish searching chats. Results already found are kept; retry to continue.
        </Alert>
      )}
      <TripPlannerDataGrid
        apiRef={gridApiRef}
        rows={rows}
        columns={columns}
        pagination
        pageSizeOptions={[20, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: CHAT_REVIEW_BATCH_SIZE } } }}
        loading={query.isLoading}
        getRowHeight={() => 'auto'}
        localeText={{
          noRowsLabel: searching
            ? 'Searching for matching chats…'
            : paused
              ? 'Search paused'
              : 'No matching chats found',
        }}
        onRowClick={({ row }) =>
          navigate(
            { pathname: '/trip-planner/chats/' + row.id, search: location.search },
            { state: { reviewIds: gridApiRef.current.getSortedRowIds().map(Number) } },
          )
        }
        sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' }, '& .MuiDataGrid-overlayWrapper': { minHeight: 80 } }}
      />
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2 }}>
        {query.hasNextPage && (
          <Button variant="contained" disabled={searching} sx={{ textTransform: 'none' }} onClick={loadMore}>
            {paused ? 'Continue searching' : 'Find more chats'}
          </Button>
        )}
        <Typography variant="caption" color="text.secondary">
          {searching
            ? 'Searching successive pages for matches…'
            : query.hasNextPage
              ? 'More chats are available to search.'
              : query.isError
                ? ''
                : 'All matching chats loaded.'}
        </Typography>
      </Stack>
      <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]}>
        <TripSpoofDialog
          openModal={Boolean(spoof.url)}
          flushSpoofingState={() => setSpoof({ url: '', name: '', copied: false })}
          spoofUrl={spoof.url}
          trip={spoof.name}
          copiedToClipboard={spoof.copied}
        />
      </PermissionedComponent>
    </>
  );
}
